// Taker /testimonial and /testimonial/:token — the capture form
// (docs/plans/testimonial-capture.md §3). Same component for both routes;
// the token path pre-fills from the request and shows the sender's note,
// the standing path resolves its brand from the Origin host server-side (no
// host→brand map here, per design — this page never guesses its own brand).
//
// Standard taker rules: no dark mode; useBrandGtm() opted in explicitly;
// mobile first (half of these will be answered from a phone after a text).
// recordLandingView() is deliberately NOT called here — it's keyed to an
// assessment slug, and this page has no assessment.

import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { BrandProvider } from './components/BrandContext';
import type { ResolvedBrand } from './types';
import { applyBranding } from './theme';
import { useBrandGtm } from './lib/useBrandGtm';

const API = import.meta.env.VITE_API_BASE_URL as string;

type GrantedRights = 'attributed-public' | 'anonymized-public' | 'internal-only';

interface FormBrand {
  slug: string;
  name: string;
  logoUrl: string | null;
  logoWhiteUrl: string | null;
  palette: Record<string, unknown> | null;
  fonts: Record<string, unknown> | null;
  designTokens: Record<string, unknown> | null;
  googleFontsUrl: string | null;
  gtmContainerId: string | null;
}

interface Release { version: string; text: string; sha256: string }

interface RequestContext {
  token: string;
  senderName: string | null;
  personalNote: string | null;
  firstName: string | null;
  email: string;
  programTag: string | null;
  /** Words the team logged from them earlier — shown for them to edit or replace. */
  prefillQuote?: string | null;
}

interface FormPayload {
  brand: FormBrand;
  release: Release;
  request: RequestContext | null;
}

function toResolvedBrand(b: FormBrand): ResolvedBrand {
  return {
    name: b.name,
    logoUrl: b.logoUrl,
    logoWhiteUrl: b.logoWhiteUrl,
    logoLinkUrl: null,
    palette: b.palette as ResolvedBrand['palette'],
    fonts: b.fonts as ResolvedBrand['fonts'],
    designTokens: b.designTokens as ResolvedBrand['designTokens'],
    googleFontsUrl: b.googleFontsUrl,
    gtmContainerId: b.gtmContainerId,
  };
}

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '11px 13px', border: '1px solid var(--color-border, #ddd)',
  borderRadius: 8, fontSize: 15, boxSizing: 'border-box', fontFamily: 'var(--font-body)',
};
const labelStyle: React.CSSProperties = { fontSize: 13, fontWeight: 600, marginBottom: 6, display: 'block' };

export default function TestimonialPage() {
  const { token } = useParams<{ token?: string }>();
  const [payload, setPayload] = useState<FormPayload | null>(null);
  const [loadErr, setLoadErr] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    setLoadErr('');
    const url = token
      ? `${API}/api/public/testimonials/form/${encodeURIComponent(token)}`
      : `${API}/api/public/testimonials/form`;
    fetch(url)
      .then(async (r) => {
        if (!r.ok) {
          const body = await r.json().catch(() => null);
          throw new Error(body?.error || 'This form is not available right now.');
        }
        return r.json();
      })
      .then((data: FormPayload) => setPayload(data))
      .catch((e: Error) => setLoadErr(e.message))
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => {
    if (payload?.brand) applyBranding(undefined, { brand: toResolvedBrand(payload.brand) as never });
  }, [payload?.brand]);

  useBrandGtm(payload?.brand.gtmContainerId ?? null, !!payload);

  if (loading) {
    return <div style={{ padding: 60, textAlign: 'center', color: '#888' }}>Loading…</div>;
  }
  if (loadErr || !payload) {
    return (
      <div style={{ maxWidth: 480, margin: '80px auto', padding: '0 20px', textAlign: 'center' }}>
        <h1 style={{ fontSize: 22, marginBottom: 10 }}>This link isn't available</h1>
        <p style={{ color: '#666' }}>{loadErr || 'Please check the link and try again.'}</p>
      </div>
    );
  }

  return (
    <BrandProvider brand={toResolvedBrand(payload.brand)}>
      <TestimonialForm payload={payload} />
    </BrandProvider>
  );
}

function TestimonialForm({ payload }: { payload: FormPayload }) {
  const { brand, release, request } = payload;
  const [firstName, setFirstName] = useState(request?.firstName ?? '');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState(request?.email ?? '');
  const [companyName, setCompanyName] = useState('');
  const [roleTitle, setRoleTitle] = useState('');
  const [testimonial, setTestimonial] = useState(request?.prefillQuote ?? '');
  const [grantedRights, setGrantedRights] = useState<GrantedRights>('attributed-public');
  const [attestHonest, setAttestHonest] = useState(false);
  const [attestAgree, setAttestAgree] = useState(false);
  const [signatureName, setSignatureName] = useState('');
  const [showRelease, setShowRelease] = useState(false);
  const [photo, setPhoto] = useState<File | null>(null);

  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [done, setDone] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setErr('');
    if (testimonial.trim().length < 40) {
      setErr('A few more words would help — at least 40 characters.');
      return;
    }
    if (!attestHonest || !attestAgree) {
      setErr('Please check both boxes above the signature.');
      return;
    }
    if (!signatureName.trim()) {
      setErr('Please type your name to sign.');
      return;
    }
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/public/testimonials/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token: request?.token ?? undefined,
          brandSlug: request ? undefined : brand.slug,
          firstName, lastName, email, companyName, roleTitle,
          testimonial, grantedRights, attestHonest, attestAgree, signatureName,
          releaseSha256: release.sha256,
          submittedAtClient: new Date().toISOString(),
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Something went wrong — please try again.');

      if (photo && data.photoToken) {
        const fd = new FormData();
        fd.append('file', photo);
        await fetch(`${API}/api/public/testimonials/photo`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${data.photoToken}` },
          body: fd,
        }).catch(() => { /* the testimonial itself is already saved; a failed photo upload isn't fatal here */ });
      }
      setDone(true);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Something went wrong — please try again.');
    } finally {
      setBusy(false);
    }
  }

  if (done) {
    return (
      <div style={{ maxWidth: 520, margin: '80px auto', padding: '0 20px', textAlign: 'center' }}>
        {brand.logoUrl && <img src={brand.logoUrl} alt={brand.name} style={{ height: 32, marginBottom: 24 }} />}
        <h1 style={{ fontSize: 24, marginBottom: 10 }}>Thank you!</h1>
        <p style={{ color: '#666', lineHeight: 1.6 }}>
          We've received your words, and we'll never use them anywhere without checking they still say what you meant.
          A copy has been emailed to you.
        </p>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 560, margin: '0 auto', padding: '40px 18px 80px' }}>
      {brand.logoUrl && <img src={brand.logoUrl} alt={brand.name} style={{ height: 32, marginBottom: 28 }} />}

      {request ? (
        <>
          <h1 style={{ fontSize: 24, fontFamily: 'var(--font-heading)', margin: '0 0 8px' }}>
            {request.senderName ? `${request.senderName} asked if you'd share a few words` : 'Share a few words'}
            {` about your experience with ${brand.name}`}
          </h1>
          {request.personalNote && (
            <p style={{ fontSize: 15, color: '#555', background: 'var(--color-tint, #f5f5f7)', padding: '12px 16px', borderRadius: 8, margin: '0 0 24px' }}>
              {request.personalNote}
            </p>
          )}
        </>
      ) : (
        <h1 style={{ fontSize: 24, fontFamily: 'var(--font-heading)', margin: '0 0 24px' }}>
          Share your experience with {brand.name}
        </h1>
      )}

      <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
        <div style={{ display: 'flex', gap: 12 }}>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>First name</label>
            <input style={inputStyle} value={firstName} onChange={(e) => setFirstName(e.target.value)} required />
          </div>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Last name</label>
            <input style={inputStyle} value={lastName} onChange={(e) => setLastName(e.target.value)} />
          </div>
        </div>
        <div>
          <label style={labelStyle}>Email</label>
          <input style={inputStyle} type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
        </div>
        <div style={{ display: 'flex', gap: 12 }}>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Firm / company (optional)</label>
            <input style={inputStyle} value={companyName} onChange={(e) => setCompanyName(e.target.value)} />
          </div>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Role / title (optional)</label>
            <input style={inputStyle} value={roleTitle} onChange={(e) => setRoleTitle(e.target.value)} />
          </div>
        </div>

        <div>
          <label style={labelStyle}>Your testimonial</label>
          <p style={{ fontSize: 12.5, color: '#888', margin: '0 0 8px' }}>
            {request?.prefillQuote
              ? 'We’ve filled in what you sent us. Change it however you like — nothing is shared until you tick the boxes below and sign.'
              : 'What was it like before? What changed? Who would you tell about this?'}
          </p>
          <textarea
            style={{ ...inputStyle, minHeight: 130, resize: 'vertical' }}
            value={testimonial}
            onChange={(e) => setTestimonial(e.target.value)}
            maxLength={1500}
            required
          />
          <div style={{ fontSize: 11, color: '#999', textAlign: 'right' }}>{testimonial.length}/1500</div>
        </div>

        <div>
          <label style={labelStyle}>A clear photo of you (optional)</label>
          <input type="file" accept="image/jpeg,image/png,image/webp" onChange={(e) => setPhoto(e.target.files?.[0] ?? null)} />
          <p style={{ fontSize: 12, color: '#999', margin: '4px 0 0' }}>The original from your photographer is perfect.</p>
        </div>

        <div>
          <label style={labelStyle}>How would you like to be credited?</label>
          {([
            ['attributed-public', 'Use my name, firm and photo'],
            ['anonymized-public', 'Use my words, but not my name'],
            ['internal-only', "Share this with the team only — please don't publish it"],
          ] as [GrantedRights, string][]).map(([value, label]) => (
            <label key={value} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, padding: '4px 0' }}>
              <input type="radio" name="grantedRights" checked={grantedRights === value} onChange={() => setGrantedRights(value)} />
              {label}
            </label>
          ))}
        </div>

        <div style={{ border: '1px solid var(--color-border, #ddd)', borderRadius: 8, padding: '14px 16px' }}>
          <button type="button" onClick={() => setShowRelease((v) => !v)} style={{ background: 'none', border: 'none', color: 'var(--color-primary)', fontSize: 13, cursor: 'pointer', padding: 0, marginBottom: showRelease ? 10 : 0 }}>
            {showRelease ? 'Hide the release' : 'Read the release'}
          </button>
          {showRelease && (
            <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', fontSize: 13, color: '#555', margin: 0 }}>{release.text}</pre>
          )}
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 13.5, marginTop: 12 }}>
            <input type="checkbox" checked={attestHonest} onChange={(e) => setAttestHonest(e.target.checked)} style={{ marginTop: 3 }} />
            This is my own honest experience, in my own words.
          </label>
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 13.5, marginTop: 8 }}>
            <input type="checkbox" checked={attestAgree} onChange={(e) => setAttestAgree(e.target.checked)} style={{ marginTop: 3 }} />
            I've read the release above and I agree to it.
          </label>
        </div>

        <div>
          <label style={labelStyle}>Type your full name to sign</label>
          <input style={inputStyle} value={signatureName} onChange={(e) => setSignatureName(e.target.value)} required />
        </div>

        {err && <div style={{ color: '#b00020', fontSize: 13.5 }}>{err}</div>}

        <button
          type="submit"
          disabled={busy}
          style={{
            background: 'var(--color-primary)', color: '#fff', border: 'none', borderRadius: 8,
            padding: '13px 20px', fontSize: 15.5, fontWeight: 700, cursor: busy ? 'default' : 'pointer',
            opacity: busy ? 0.7 : 1,
          }}
        >
          {busy ? 'Sending…' : 'Submit'}
        </button>
      </form>
    </div>
  );
}
